import { Select, Tag } from "antd";

const EMPTY_LOCATION = { city_code: "", area_codes: [] };

/**
 * Props:
 *   value         : { city_code: string, area_codes: string[] }
 *   onChange      : (value) => void
 *   onRemove      : () => void  (undefined = no remove button)
 *   index         : number
 *   cityOptions   : [{ label, value }]
 *   getAreaOptions: (cityCode) => [{ label, value }]
 *   loading       : boolean
 */
const LocationPicker = ({
  value = EMPTY_LOCATION,
  onChange,
  onRemove,
  index,
  cityOptions = [],
  getAreaOptions,
  loading = false,
}) => {
  const areaOptions = value.city_code
    ? (getAreaOptions?.(value.city_code) ?? [])
    : [];

  const update = (patch) => onChange({ ...value, ...patch });

  const removeArea = (areaCode) =>
    update({
      area_codes: value.area_codes.filter((code) => code !== areaCode),
    });

  return (
    <div
      style={{
        borderRadius: 8,
        padding: "14px 14px 12px",
        marginBottom: 12,
        position: "relative",
      }}
    >
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          style={{
            position: "absolute",
            top: 8,
            right: 10,
            background: "none",
            border: "none",
            cursor: "pointer",
            fontSize: 20,
            lineHeight: 1,
            color: "#aaa",
          }}
          title="Remove this location"
        >
          ×
        </button>
      )}

      <div style={{ marginBottom: 14 }}>
        <label
          style={{
            display: "block",
            fontSize: 13,
            fontWeight: 500,
            marginBottom: 4,
          }}
        >
          City {index + 1} <span style={{ color: "red" }}>*</span>
        </label>
        <Select
          showSearch
          value={value.city_code || undefined}
          options={cityOptions}
          loading={loading}
          placeholder="Select city"
          style={{ width: "100%" }}
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
          onChange={(cityCode) =>
            update({ city_code: cityCode, area_codes: [] })
          }
          allowClear
          onClear={() => update(EMPTY_LOCATION)}
        />
      </div>

      <div>
        <label
          style={{
            display: "block",
            fontSize: 13,
            fontWeight: 500,
            marginBottom: 4,
          }}
        >
          Areas <span style={{ color: "red" }}>*</span>
          <span style={{ fontWeight: 400, color: "#888", marginLeft: 6 }}>
            {value.city_code
              ? "(select one or more areas in this city)"
              : "(select a city first)"}
          </span>
        </label>

        <Select
          mode="multiple"
          showSearch
          value={value.area_codes}
          options={areaOptions}
          loading={loading}
          disabled={!value.city_code}
          placeholder={
            value.city_code ? "Select areas" : "Select a city first"
          }
          style={{ width: "100%", marginBottom: 8 }}
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
          onChange={(areaCodes) => update({ area_codes: areaCodes })}
        />

        {value.area_codes.length > 0 ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {value.area_codes.map((areaCode) => {
              const label =
                areaOptions.find((opt) => opt.value === areaCode)?.label ??
                areaCode;
              return (
                <Tag
                  key={areaCode}
                  closable
                  onClose={() => removeArea(areaCode)}
                  style={{ borderRadius: 12, fontSize: 12 }}
                >
                  {label}
                </Tag>
              );
            })}
          </div>
        ) : (
          <div style={{ fontSize: 12, color: "#bbb" }}>No areas selected.</div>
        )}
      </div>
    </div>
  );
};

export default LocationPicker;
